import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import WerkorderModal from './WerkorderModal';
import './RestwerkDashboard.css';
import './WerkorderModal.css';
import api from '../services/api';

const RestwerkDashboard = () => {
  const { terms } = useAuth();
  const { gebruiker, logout } = useAuth();
  const navigate = useNavigate();
  const [restwerk, setRestwerk] = useState([]);
  const [afdelingen, setAfdelingen] = useState([]);
  const [gebruikers, setGebruikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showWerkorderModal, setShowWerkorderModal] = useState(false);
  const [selectedRestwerk, setSelectedRestwerk] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [werkorderData, setWerkorderData] = useState({
    titel: '', beschrijving: '', afdeling_id: '', prioriteit: 'normaal', toegewezen_aan: '', ordernummer: ''
  });

  // Bewerken state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ id: null, beschrijving: '', restwerk_beschrijving: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editFotos, setEditFotos] = useState([]);
  const [editUploading, setEditUploading] = useState(false);
  // Restwerk → werkorder(s): multi-select afdelingen
  // { [afd_id]: { beschrijving, toegewezen_aan } } — aanwezig = geselecteerd
  const [afdelingsSelectie, setAfdelingsSelectie] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [restwerkRes, afdelingenRes, gebruikersRes] = await Promise.all([
        api.get('/klanten/restwerk/overzicht'),
        api.get('/afdelingen'),
        api.get('/gebruikers')
      ]);
      setRestwerk(restwerkRes.data.restwerk || []);
      setAfdelingen(afdelingenRes.data.afdelingen || []);
      setGebruikers(gebruikersRes.data.gebruikers || []);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij laden restwerk:', error);
      setLoading(false);
    }
  };

  const handleDeleteRestwerk = async (rwId) => {
    try {
      await api.delete(`/klanten/restwerk/${rwId}`);
      setRestwerk(prev => prev.filter(r => r.id !== rwId));
      setConfirmDeleteId(null);
    } catch (error) {
      alert("Fout bij verwijderen");
    }
  };

  const handlePrintPDF = async (rwId) => {
    try {
      const response = await api.get('/klanten/restwerk/' + rwId + '/pdf', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Cleanup na 1 minuut
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      alert('Fout bij ophalen PDF: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEditOpen = async (rw) => {
    setEditData({
      id: rw.id,
      beschrijving: rw.beschrijving || '',
      restwerk_beschrijving: rw.restwerk_beschrijving || ''
    });
    setEditFotos([]);
    setShowEditModal(true);
    // Foto's laden
    try {
      const res = await api.get('/klanten/restwerk/' + rw.id + '/fotos');
      setEditFotos(res.data.fotos || []);
    } catch (e) {
      console.error('Fout bij laden fotos:', e);
    }
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editData.id) return;
    setEditUploading(true);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      const res = await api.post('/klanten/restwerk/' + editData.id + '/foto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditFotos(prev => [...prev, res.data.foto]);
      // Update count in lijst
      setRestwerk(prev => prev.map(r => r.id === editData.id
        ? { ...r, aantal_fotos: (parseInt(r.aantal_fotos) || 0) + 1 }
        : r
      ));
    } catch (err) {
      alert('Fout bij uploaden foto: ' + (err.response?.data?.message || err.message));
    } finally {
      setEditUploading(false);
      e.target.value = '';
    }
  };

  const handleFotoDelete = async (fotoId) => {
    if (!window.confirm('Foto verwijderen?')) return;
    try {
      await api.delete('/klanten/restwerk/foto/' + fotoId);
      setEditFotos(prev => prev.filter(f => f.id !== fotoId));
      setRestwerk(prev => prev.map(r => r.id === editData.id
        ? { ...r, aantal_fotos: Math.max(0, (parseInt(r.aantal_fotos) || 0) - 1) }
        : r
      ));
    } catch (err) {
      alert('Fout bij verwijderen foto: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`/klanten/restwerk/${editData.id}`, {
        beschrijving: editData.beschrijving,
        restwerk_beschrijving: editData.restwerk_beschrijving
      });
      setRestwerk(prev => prev.map(r => r.id === editData.id
        ? { ...r, beschrijving: editData.beschrijving, restwerk_beschrijving: editData.restwerk_beschrijving }
        : r
      ));
      setShowEditModal(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Fout bij bewerken');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreateWerkorder = (rw) => {
    setSelectedRestwerk(rw);
    setWerkorderData({
      titel: `Restwerk: ${rw.klant_naam}`,
      beschrijving: [
        rw.restwerk_beschrijving ? `RESTWERK (nog te doen):\n${rw.restwerk_beschrijving}` : '',
        rw.beschrijving ? `\nOorspronkelijke melding:\n${rw.beschrijving}` : ''
      ].filter(Boolean).join('\n').trim(),
      afdeling_id: '', prioriteit: 'normaal', toegewezen_aan: '', ordernummer: ''
    });
    setAfdelingsSelectie({});
    setShowModal(true);
  };
  const toggleAfdelingSelectie = (afdId) => {
    setAfdelingsSelectie(prev => {
      const next = { ...prev };
      if (next[afdId]) {
        delete next[afdId];
      } else {
        next[afdId] = {
          beschrijving: [
            selectedRestwerk?.restwerk_beschrijving ? `RESTWERK (nog te doen):\n${selectedRestwerk.restwerk_beschrijving}` : '',
            selectedRestwerk?.beschrijving ? `\nOorspronkelijke melding:\n${selectedRestwerk.beschrijving}` : ''
          ].filter(Boolean).join('\n').trim(),
          toegewezen_aan: [],
          geplande_datum: ''
        };
      }
      return next;
    });
  };
  const updateAfdelingVeld = (afdId, veld, waarde) => {
    setAfdelingsSelectie(prev => ({
      ...prev,
      [afdId]: { ...(prev[afdId] || {}), [veld]: waarde }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const afdIds = Object.keys(afdelingsSelectie);
    if (afdIds.length === 0) { alert('Selecteer minimaal één afdeling'); return; }
    if (!werkorderData.titel) { alert('Titel is verplicht'); return; }
    try {
      for (const afdId of afdIds) {
        const sel = afdelingsSelectie[afdId];
        await api.post('/werkorders', {
          titel: werkorderData.titel,
          beschrijving: sel.beschrijving || '',
          prioriteit: werkorderData.prioriteit || 'normaal',
          ordernummer: werkorderData.ordernummer || null,
          afdeling_id: parseInt(afdId, 10),
          toegewezen_aan: (sel.toegewezen_aan || []).map(Number),
          geplande_datum: sel.geplande_datum || null,
          klant_id: selectedRestwerk.klant_id,
          locatie_adres: selectedRestwerk.klant_adres,
          restwerk_log_id: selectedRestwerk.id
        });
      }
      setShowModal(false);
      setSelectedRestwerk(null);
      setWerkorderData({ titel: '', beschrijving: '', afdeling_id: '', prioriteit: 'normaal', toegewezen_aan: '', ordernummer: '' });
      setAfdelingsSelectie({});
      loadData();
      alert(`${afdIds.length} werkorder${afdIds.length > 1 ? 's' : ''} succesvol aangemaakt!`);
    } catch (error) {
      alert(error.response?.data?.message || 'Fout bij aanmaken werkorders');
    }
  };

  // MARKER-ADMIN-VERWERKT-FR
  const toggleAdminVerwerkt = async (logId, verwerkt) => {
    try {
      await api.put(`/klanten/restwerk/${logId}/administratief`, { verwerkt });
      loadData();
    } catch (e) {
      alert('Fout bij bijwerken');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="loading-screen">Laden...</div>;
  }

  return (
    <div className="restwerk-dashboard">

      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>⚠️ Restwerk Dashboard</h1>
          <div className="header-actions">
          </div>
        </div>

        <div className="dashboard-stats">
          <div className="stat-card stat-restwerk">
            <div className="stat-number">{restwerk.length}</div>
            <div className="stat-label">Openstaand Restwerk</div>
          </div>
        </div>

        <div className="werkorders-section">
          <h2>Restwerk Items</h2>
          {restwerk.length === 0 ? (
            <div className="no-werkorders">
              <p>🎉 Geen openstaand restwerk!</p>
            </div>
          ) : (
            <div className="werkorders-list">
              {restwerk.map(rw => (
                <div key={rw.id} className="werkorder-card restwerk-item">
                  <div style={{padding:'8px 12px',background:rw.administratief_verwerkt?'#d1fae5':'#fef3c7',borderBottom:'1px solid '+(rw.administratief_verwerkt?'#10b981':'#fbbf24'),display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    {rw.administratief_verwerkt ? (
                      <>
                        <span style={{color:'#047857',fontWeight:600,fontSize:13}}>✓ Administratief verwerkt{rw.administratief_verwerkt_op?' · '+new Date(rw.administratief_verwerkt_op).toLocaleDateString('nl-NL'):''}</span>
                        <button onClick={()=>toggleAdminVerwerkt(rw.id,false)} style={{background:'transparent',border:'1px solid #10b981',color:'#047857',borderRadius:6,padding:'3px 10px',cursor:'pointer',fontSize:12}}>Ongedaan maken</button>
                      </>
                    ) : (
                      <>
                        <span style={{color:'#92400e',fontSize:13}}>Nog niet administratief verwerkt</span>
                        <button onClick={()=>toggleAdminVerwerkt(rw.id,true)} style={{background:'#10b981',color:'white',border:'none',borderRadius:6,padding:'4px 12px',cursor:'pointer',fontSize:12,fontWeight:600}}>✓ Markeer als verwerkt</button>
                      </>
                    )}
                  </div>
                  <div className="werkorder-header">
                    <div className="werkorder-title-row">
                      <h3>⚠️ {rw.klant_naam}</h3>
                      <span className="restwerk-date">📅 {new Date(rw.created_at).toLocaleDateString('nl-NL')}</span>
                    </div>
                  </div>
                  {rw.restwerk_beschrijving && (
                    <div style={{background:'#fff7ed',border:'1px solid #fdba74',borderRadius:8,padding:'8px 12px',margin:'8px 0'}}>
                      <div style={{fontWeight:700,color:'#9a3412',fontSize:13,marginBottom:2}}>🚩 Nog te doen (restwerk)</div>
                      <div style={{whiteSpace:'pre-wrap',color:'#7c2d12',fontSize:14}}>{rw.restwerk_beschrijving}</div>
                    </div>
                  )}
                  {rw.beschrijving && (<p className="werkorder-beschrijving"><span style={{fontWeight:600,color:'#6b7280',fontSize:12}}>Logboek: </span>{rw.beschrijving}</p>)}
                  <div className="werkorder-meta">
                  <span>👤 Gemeld door: {rw.gebruiker_naam || ((rw.voornaam || '') + ' ' + (rw.achternaam || '')).trim() || '-'}</span>
                  </div>
                  <div className="restwerk-actions">
                    <button onClick={() => handlePrintPDF(rw.id)} className="btn-secondary">🖨️ Print PDF</button>
                    <button onClick={() => handleEditOpen(rw)} className="btn-secondary">✏️ Bewerken</button>
                    <button onClick={() => handleCreateWerkorder(rw)} className="btn-primary">📋 Maak Werkorder</button>
                    <button onClick={() => navigate(`/klant/${rw.klant_id}`)} className="btn-secondary">👁️ Bekijk Klant</button>
                    {confirmDeleteId === rw.id ? (
                      <>
                        <button onClick={() => handleDeleteRestwerk(rw.id)} className="btn-secondary" style={{color: "white", background: "#dc2626", borderColor: "#dc2626"}}>✓ Ja, verwijder</button>
                        <button onClick={() => setConfirmDeleteId(null)} className="btn-secondary">✗ Nee</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDeleteId(rw.id)} className="btn-secondary" style={{color: "red"}}>🗑️ Verwijder</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Restwerk Bewerken</h2>
              <button onClick={() => setShowEditModal(false)} className="btn-close">×</button>
            </div>
            <form onSubmit={handleEditSave}>
              <div className="form-group">
                <label>Logboek tekst</label>
                <textarea
                  value={editData.beschrijving}
                  onChange={(e) => setEditData({ ...editData, beschrijving: e.target.value })}
                  rows="4"
                  placeholder="Wat is er gedaan?"
                />
              </div>
              <div className="form-group">
                <label>Restwerk omschrijving</label>
                <textarea
                  value={editData.restwerk_beschrijving}
                  onChange={(e) => setEditData({ ...editData, restwerk_beschrijving: e.target.value })}
                  rows="4"
                  placeholder="Wat moet er nog gedaan worden?"
                />
              </div>

              <div className="form-group">
                <label>Foto's ({editFotos.length})</label>
                {editFotos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
                    {editFotos.map(f => (
                      <div key={f.id} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <img
                          src={f.url || '/uploads/klant_logs/' + f.bestand_naam}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          onClick={() => handleFotoDelete(f.id)}
                          style={{
                            position: 'absolute', top: 4, right: 4,
                            width: 24, height: 24, borderRadius: '50%',
                            background: 'rgba(220, 38, 38, 0.9)', color: 'white',
                            border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}
                          title="Foto verwijderen"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
                <label
                  htmlFor="restwerk-foto-upload"
                  style={{
                    display: 'inline-block', padding: '10px 16px',
                    background: editUploading ? '#cbd5e1' : '#00D4AA', color: 'white',
                    borderRadius: 8, cursor: editUploading ? 'not-allowed' : 'pointer',
                    fontSize: 14, fontWeight: 600
                  }}
                >
                  {editUploading ? 'Uploaden...' : '📷 Foto toevoegen'}
                </label>
                <input
                  id="restwerk-foto-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFotoUpload}
                  disabled={editUploading}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn-secondary" disabled={editSaving}>Annuleren</button>
                <button type="submit" className="btn-primary" disabled={editSaving}>{editSaving ? 'Opslaan...' : '💾 Opslaan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content restwerk-wo-modal" style={{ maxWidth: 960, width: "95vw" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Werkorder Aanmaken voor Restwerk</h2>
              <button onClick={() => setShowModal(false)} className="btn-close">×</button>
            </div>
            <div className="restwerk-info">
              <p><strong>{terms?.klant_singular || "Klant"}:</strong> {selectedRestwerk?.klant_naam}</p>
              {(selectedRestwerk?.beschrijving || selectedRestwerk?.restwerk_beschrijving) && (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
                  <div style={{ fontWeight: 600, color: '#92400e', fontSize: 13, marginBottom: 4 }}>💬 Wat de monteur noteerde:</div>
                  <div style={{ whiteSpace: 'pre-wrap', color: '#451a03', fontSize: 14, userSelect: 'text' }}>
                    {selectedRestwerk.beschrijving || selectedRestwerk.restwerk_beschrijving}
                  </div>
                </div>
              )}
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Titel * <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>(geldt voor alle aangemaakte werkorders)</span></label>
                <input type="text" value={werkorderData.titel} onChange={(e) => setWerkorderData({...werkorderData, titel: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Ordernummer <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>(optioneel)</span></label>
                <input type="text" value={werkorderData.ordernummer || ''} onChange={(e) => setWerkorderData({...werkorderData, ordernummer: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Prioriteit</label>
                <select value={werkorderData.prioriteit} onChange={(e) => setWerkorderData({...werkorderData, prioriteit: e.target.value})}>
                  <option value="laag">🟢 Laag</option>
                  <option value="normaal">🟡 Normaal</option>
                  <option value="hoog">🔴 Hoog</option>
                </select>
              </div>
              <div className="form-group">
                <label>Afdelingen * <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>(1 werkorder per gekozen afdeling)</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {afdelingen.map(afd => {
                    const actief = !!afdelingsSelectie[afd.id];
                    return (
                      <button type="button" key={afd.id} onClick={() => toggleAfdelingSelectie(afd.id)}
                        style={{
                          padding: '6px 14px', borderRadius: 999,
                          border: actief ? '2px solid #2563eb' : '1px solid #d1d5db',
                          background: actief ? '#dbeafe' : '#fff',
                          color: actief ? '#1e3a8a' : '#374151',
                          fontWeight: actief ? 600 : 500, cursor: 'pointer', fontSize: 14
                        }}>
                        {actief ? '✓ ' : ''}{afd.naam}
                      </button>
                    );
                  })}
                </div>
                {Object.keys(afdelingsSelectie).length === 0 && (
                  <div style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', padding: '8px 12px', borderRadius: 6 }}>
                    Selecteer minimaal één afdeling om door te sturen.
                  </div>
                )}
              </div>
              {Object.keys(afdelingsSelectie).map(afdId => {
                const afd = afdelingen.find(a => String(a.id) === String(afdId));
                if (!afd) return null;
                const sel = afdelingsSelectie[afdId];
                return (
                  <div key={afdId} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, background: '#f9fafb' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#1f2937' }}>📋 {afd.naam}</div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 13 }}>Beschrijving voor {afd.naam}</label>
                      <textarea value={sel.beschrijving || ''} onChange={(e) => updateAfdelingVeld(afdId, 'beschrijving', e.target.value)} rows="8" style={{ minHeight: 160, resize: 'vertical', width: '100%' }} placeholder={`Wat moet ${afd.naam} doen?`} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 13 }}>Geplande datum (optioneel)</label>
                      <input type="date" value={sel.geplande_datum || ''} onChange={(e) => updateAfdelingVeld(afdId, 'geplande_datum', e.target.value)} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: 13 }}>Monteurs (optioneel){(sel.toegewezen_aan||[]).length > 0 && <span className="wo-badge-green" style={{ marginLeft: 8 }}>✓ {sel.toegewezen_aan.length} geselecteerd</span>}</label>
                      <div className="wo-monteur-grid">
                        {gebruikers.filter(g => g.actief && String(g.afdeling_id) === String(afdId) && !String(g.gebruikersnaam || '').startsWith('oa_')).map(gbr => {
                          const list = sel.toegewezen_aan || [];
                          const checked = list.includes(gbr.id);
                          return (
                            <label key={gbr.id} className={`wo-monteur-item ${checked ? 'selected' : ''}`}>
                              <input type="checkbox" checked={checked} onChange={() => updateAfdelingVeld(afdId, 'toegewezen_aan', checked ? list.filter(x => x !== gbr.id) : [...list, gbr.id])} />
                              <span className="wo-monteur-name">{gbr.voornaam} {gbr.achternaam}</span>
                              <span className="wo-monteur-rol">{gbr.rol}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Annuleren</button>
                <button type="submit" className="btn-primary" disabled={Object.keys(afdelingsSelectie).length === 0}>
                  {Object.keys(afdelingsSelectie).length > 1
                    ? `${Object.keys(afdelingsSelectie).length} Werkorders Aanmaken`
                    : 'Werkorder Aanmaken'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <WerkorderModal show={showWerkorderModal} onClose={() => setShowWerkorderModal(false)} onSuccess={() => setShowWerkorderModal(false)} />
    </div>
  );
};

export default RestwerkDashboard;
